import { useState, useEffect, ReactElement, useRef } from 'react'
import {
    useFloating,
    autoUpdate,
    offset,
    shift,
    useHover,
    useFocus,
    flip,
    useDismiss,
    useInteractions,
    useClick,
    FloatingPortal,
    FloatingArrow,
    Placement,
    useTransitionStatus,
    arrow,
    safePolygon,
} from '@floating-ui/react'
import CrossIcon from '../assets/cross.svg'
import styles from './Tooltip.module.scss'
import clsx from 'clsx'

interface Props {
    children: ReactElement
    tooltip: ReactElement
    placement?: Placement
    offset?: number
    crossAxis?: number
    trigger?: 'hover' | 'click' | 'manual'
    isShown?: boolean
    className?: string
}

export const Tooltip = ({
    children,
    placement = 'bottom',
    trigger = 'hover',
    offset: _offset = 10,
    crossAxis: _crossAxis = 0,
    isShown = true,
    className,
    tooltip,
}: Props) => {
    const arrowRef = useRef(null)
    const [open, setOpen] = useState(false)
    const { x, y, refs, strategy, context } = useFloating({
        open,
        onOpenChange: (state) => {
            if (isShown && trigger !== 'manual') {
                setOpen(state)
            }
        },
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset({
                mainAxis: _offset,
                crossAxis: _crossAxis,
            }),
            flip(),
            shift(),
            arrow({
                element: arrowRef,
            }),
        ],
    })

    const hover = useHover(context, {
        move: false,
        enabled: trigger === 'hover',
        handleClose: safePolygon(),
    })

    const focus = useFocus(context)
    const dismiss = useDismiss(context)
    const click = useClick(context, { enabled: trigger === 'click' })
    const { isMounted, status } = useTransitionStatus(context)
    const { getReferenceProps, getFloatingProps } = useInteractions([focus, dismiss, hover, click])

    useEffect(() => {
        if (trigger === 'manual') {
            setOpen(isShown)
        }
    }, [trigger, isShown])

    return (
        <>
            <div ref={refs.setReference} {...getReferenceProps()}>
                {children}
            </div>
            {isMounted && isShown && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className={clsx(styles.container, className)}
                        style={{
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                        }}
                        data-status={status}
                        {...getFloatingProps({ onClick: (e) => e.stopPropagation() })}
                    >
                        <FloatingArrow ref={arrowRef} context={context} />
                        {tooltip}

                        {trigger === 'click' && (
                            <button className={styles.closeIcon} onClick={() => setOpen(false)}>
                                <CrossIcon />
                            </button>
                        )}
                    </div>
                </FloatingPortal>
            )}
        </>
    )
}
